import { useState } from 'react';
import { RectangleStackIcon } from '@heroicons/react/24/outline';
import {
  useDeleteEndpointTestSuite,
  useEndpointTestSuites,
  useUpdateEndpointTestSuite,
} from '@/hooks/useEndpointTestEvals';
import { useToast } from '@/contexts/ToastContext';
import { useInFlightTestIds } from '@/hooks/useEndpointTestRunGroups';
import { useFlag } from '@/hooks/useFeatureFlags';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  NOTHING_RELEASED,
  RunOptionsControls,
  resolveRunTarget,
  runTargetsBlocked,
  type RunTargets,
  type RunToolbarOptions,
} from './RunOptionsControls';
import { SuiteMembershipDialog } from './SuiteMembershipDialog';
import type { EndpointTest, EndpointTestSuite } from '@/types/endpointTests';

interface SuitesBlockProps {
  endpointId: string;
  /** Every test for the endpoint, in list order — the membership dialog's rows. */
  tests: EndpointTest[];
  /** The run target rule's inputs (task 1236); null while the versioning feature is off. */
  targets?: RunTargets | null;
  /** Start one test run of the suite's tests with the block's run settings. */
  onRun: (suite: EndpointTestSuite, options: RunToolbarOptions) => void;
  /** A test run is starting: Run waits. */
  running: boolean;
}

/**
 * Named suites (task 1180), run as a unit (task 1209). A suite's Run starts
 * one test run of its current members with the block's own **Run on** and
 * **At a time** (shared by every suite here), without touching the list's
 * selection; **Edit tests** opens the membership dialog. Renders nothing until
 * a suite exists.
 */
export function SuitesBlock(props: SuitesBlockProps) {
  // Suites run as a unit, so they answer to the test runs lever (task 1215).
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  return runsEnabled ? <SuitesBlockContent {...props} /> : null;
}

function SuitesBlockContent({ endpointId, tests, targets, onRun, running }: SuitesBlockProps) {
  const { data: suites = [] } = useEndpointTestSuites(endpointId);
  const updateSuite = useUpdateEndpointTestSuite(endpointId);
  const deleteSuite = useDeleteEndpointTestSuite(endpointId);
  const toast = useToast();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState<EndpointTestSuite | null>(null);
  const [editingMembers, setEditingMembers] = useState<EndpointTestSuite | null>(null);
  const [runOptions, setRunOptions] = useState<RunToolbarOptions>({ environmentId: null, concurrency: 1 });
  const busyCount = (suite: EndpointTestSuite) => suite.testIds.filter((id) => inFlight.has(id)).length;
  // The block's own target (working state without a picker), checked against
  // the scenarios already running there (task 1229).
  const inFlight = useInFlightTestIds(endpointId, resolveRunTarget(targets, runOptions.environmentId));
  const nothingReleased = runTargetsBlocked(targets);

  if (suites.length === 0) return null;

  const saveName = async (suite: EndpointTestSuite) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === suite.name) {
      setRenamingId(null);
      return;
    }
    try {
      await updateSuite.mutateAsync({ id: suite.id, data: { name: trimmed } });
      setRenamingId(null);
    } catch (err) {
      toast.error('Failed to rename suite', err instanceof Error ? err.message : undefined);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteSuite.mutateAsync(deleting.id);
      toast.success('Suite deleted', deleting.name);
      setDeleting(null);
    } catch (err) {
      toast.error('Failed to delete suite', err instanceof Error ? err.message : undefined);
    }
  };

  const action = 'text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800';

  return (
    <div data-testid="suites-block" className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-1">
        <p className="flex items-center text-xs font-medium text-gray-500 dark:text-gray-400">
          <RectangleStackIcon className="h-4 w-4 mr-1" />
          Suites
        </p>
        <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
          <RunOptionsControls options={runOptions} onOptionsChange={setRunOptions} targets={targets} />
        </span>
      </div>
      <ul className="space-y-1">
        {suites.map((suite) => (
          <li key={suite.id} data-testid={`suite-${suite.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {renamingId === suite.id ? (
              <input
                aria-label="Suite name"
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveName(suite);
                  if (e.key === 'Escape') setRenamingId(null);
                }}
                onBlur={() => void saveName(suite)}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-0.5 text-sm text-gray-900 dark:text-white"
              />
            ) : (
              <span className="font-medium text-gray-900 dark:text-white">{suite.name}</span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {suite.testIds.length} {suite.testIds.length === 1 ? 'scenario' : 'scenarios'}
            </span>
            <span className="ml-auto flex items-center gap-3">
              <button
                type="button"
                onClick={() => onRun(suite, runOptions)}
                disabled={running || suite.testIds.length === 0 || busyCount(suite) > 0 || nothingReleased}
                title={
                  nothingReleased
                    ? NOTHING_RELEASED
                    : busyCount(suite) > 0
                    ? `${busyCount(suite)} of this suite's scenarios ${busyCount(suite) === 1 ? 'is' : 'are'} already running in a test on this target`
                    : undefined
                }
                className={`${action} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Run
              </button>
              <button
                type="button"
                onClick={() => {
                  setName(suite.name);
                  setRenamingId(suite.id);
                }}
                className={action}
              >
                Rename
              </button>
              <button type="button" onClick={() => setEditingMembers(suite)} className={action}>Edit scenarios</button>
              <button type="button" onClick={() => setDeleting(suite)} className="text-xs font-medium text-gray-500 hover:text-red-600">
                Delete
              </button>
            </span>
          </li>
        ))}
      </ul>
      {editingMembers && (
        <SuiteMembershipDialog
          endpointId={endpointId}
          suite={editingMembers}
          tests={tests}
          onClose={() => setEditingMembers(null)}
        />
      )}
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete suite"
        message={`Delete the suite "${deleting?.name ?? ''}"? Its scenarios stay; only the saved selection goes.`}
        confirmLabel="Delete"
        confirmStyle="danger"
        isLoading={deleteSuite.isPending}
      />
    </div>
  );
}

export default SuitesBlock;
